import type { ComponentType } from 'react';
import { Badge } from '../ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { cn } from '../../lib/utils';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from './ResourcePreviewCard';
import { ResourcePreview } from './ResourcePreview';

export interface ResourceChipProps {
  kind: string;
  id: string;
  name?: string;
  className?: string;
  'data-testid'?: string;
  /** Optional leading icon (e.g. the entity kind's glyph) shown before the name. */
  icon?: ComponentType<{ className?: string }>;
}

export function ResourceChip({
  kind,
  id,
  name,
  className,
  'data-testid': dataTestId,
  icon: Icon,
}: ResourceChipProps) {
  const displayName = name ?? id;
  const isIdFallback = !name;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`Preview ${kind} ${displayName}`}
          data-testid={dataTestId ?? `resource-chip-${kind}-${id}`}
          title={displayName}
          className="inline-flex items-center rounded-md align-middle transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge
            variant="outline"
            className={cn('cursor-default gap-1 px-1.5 py-0 text-[10px] font-medium', className)}
          >
            {Icon ? <Icon className="h-3 w-3 shrink-0 opacity-70" /> : null}
            <span className={cn('max-w-[16rem] truncate', isIdFallback && 'font-mono')}>
              {displayName}
            </span>
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={`${kind} ${displayName}`}
      >
        <ResourcePreview kind={kind} id={id} fallbackName={displayName} />
      </HoverCardContent>
    </HoverCard>
  );
}
